import type { ValueOf } from 'type-fest';

import { ComponentSubtypes } from './components';
import { ServiceSubtypes } from './services';

// Data type enum for capture methods
export enum CaptureDataType {
  TABULAR = 'TABULAR',
  BINARY = 'BINARY',
}

export const CaptureMethodNames = {
  Analogs: 'Analogs',
  AngularVelocity: 'AngularVelocity',
  CompassHeading: 'CompassHeading',
  Current: 'Current',
  EndPosition: 'EndPosition',
  GetAudio: 'GetAudio',
  GetWorldPose: 'GetWorldPose',
  GetImages: 'GetImages',
  Gpios: 'Gpios',
  IsHoldingSomething: 'IsHoldingSomething',
  IsPowered: 'IsPowered',
  JointPositions: 'JointPositions',
  Lengths: 'Lengths',
  LinearAcceleration: 'LinearAcceleration',
  LinearVelocity: 'LinearVelocity',
  NextPointCloud: 'NextPointCloud',
  Orientation: 'Orientation',
  PointCloudMap: 'PointCloudMap',
  Position: 'Position',
  Power: 'Power',
  ReadImage: 'ReadImage',
  Readings: 'Readings',
  TicksCount: 'TicksCount',
  Voltage: 'Voltage',
  CaptureAllFromCamera: 'CaptureAllFromCamera',
  DoCommand: 'DoCommand',
} as const;

export type CaptureMethodName = ValueOf<typeof CaptureMethodNames>;

export const AllCaptureMethodNames = Object.values(CaptureMethodNames) as [
  CaptureMethodName,
  ...CaptureMethodName[],
];

// Mapping of capture methods to their data types
export const CaptureMethodDataTypes: Record<
  CaptureMethodName,
  CaptureDataType
> = {
  [CaptureMethodNames.Analogs]: CaptureDataType.TABULAR,
  [CaptureMethodNames.AngularVelocity]: CaptureDataType.TABULAR,
  [CaptureMethodNames.CompassHeading]: CaptureDataType.TABULAR,
  [CaptureMethodNames.Current]: CaptureDataType.TABULAR,
  [CaptureMethodNames.EndPosition]: CaptureDataType.TABULAR,
  [CaptureMethodNames.GetAudio]: CaptureDataType.BINARY,
  [CaptureMethodNames.GetWorldPose]: CaptureDataType.TABULAR,
  [CaptureMethodNames.GetImages]: CaptureDataType.BINARY,
  [CaptureMethodNames.Gpios]: CaptureDataType.TABULAR,
  [CaptureMethodNames.IsHoldingSomething]: CaptureDataType.TABULAR,
  [CaptureMethodNames.IsPowered]: CaptureDataType.TABULAR,
  [CaptureMethodNames.JointPositions]: CaptureDataType.TABULAR,
  [CaptureMethodNames.Lengths]: CaptureDataType.TABULAR,
  [CaptureMethodNames.LinearAcceleration]: CaptureDataType.TABULAR,
  [CaptureMethodNames.LinearVelocity]: CaptureDataType.TABULAR,
  [CaptureMethodNames.NextPointCloud]: CaptureDataType.BINARY,
  [CaptureMethodNames.Orientation]: CaptureDataType.TABULAR,
  [CaptureMethodNames.PointCloudMap]: CaptureDataType.BINARY,
  [CaptureMethodNames.Position]: CaptureDataType.TABULAR,
  [CaptureMethodNames.Power]: CaptureDataType.TABULAR,
  [CaptureMethodNames.ReadImage]: CaptureDataType.BINARY,
  [CaptureMethodNames.Readings]: CaptureDataType.TABULAR,
  [CaptureMethodNames.TicksCount]: CaptureDataType.TABULAR,
  [CaptureMethodNames.Voltage]: CaptureDataType.TABULAR,
  [CaptureMethodNames.CaptureAllFromCamera]: CaptureDataType.BINARY,
  [CaptureMethodNames.DoCommand]: CaptureDataType.TABULAR,
};

// Helper function to check if a capture method produces tabular data
export const isTabularCaptureMethod = (
  captureMethod: CaptureMethodName | undefined
): boolean => {
  if (!captureMethod) return false;
  return CaptureMethodDataTypes[captureMethod] === CaptureDataType.TABULAR;
};

export const isClipDurationCapture = (
  resourceSubtype: string | undefined,
  captureMethod: CaptureMethodName | undefined
): boolean =>
  resourceSubtype === ComponentSubtypes.AUDIO_IN &&
  captureMethod !== CaptureMethodNames.DoCommand;

export const CaptureMethodsBySubtype: Record<string, CaptureMethodName[]> = {
  [ComponentSubtypes.AUDIO_IN]: [
    CaptureMethodNames.GetAudio,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.AUDIO_OUT]: [CaptureMethodNames.GetWorldPose],
  [ComponentSubtypes.ARM]: [
    CaptureMethodNames.JointPositions,
    CaptureMethodNames.EndPosition,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.BASE]: [
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.BOARD]: [
    CaptureMethodNames.Analogs,
    CaptureMethodNames.Gpios,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.BUTTON]: [
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.CAMERA]: [
    CaptureMethodNames.GetImages,
    CaptureMethodNames.NextPointCloud,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
    CaptureMethodNames.ReadImage,
  ],
  [ComponentSubtypes.ENCODER]: [
    CaptureMethodNames.TicksCount,
    CaptureMethodNames.GetWorldPose,
  ],
  [ComponentSubtypes.GANTRY]: [
    CaptureMethodNames.Position,
    CaptureMethodNames.Lengths,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.GENERIC]: [
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.GRIPPER]: [
    CaptureMethodNames.IsHoldingSomething,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.INPUT_CONTROLLER]: [
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.MOTOR]: [
    CaptureMethodNames.Position,
    CaptureMethodNames.IsPowered,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.MOVEMENT_SENSOR]: [
    CaptureMethodNames.Readings,
    CaptureMethodNames.AngularVelocity,
    CaptureMethodNames.CompassHeading,
    CaptureMethodNames.LinearAcceleration,
    CaptureMethodNames.LinearVelocity,
    CaptureMethodNames.Orientation,
    CaptureMethodNames.Position,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.POSE_TRACKER]: [
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.POWER_SENSOR]: [
    CaptureMethodNames.Readings,
    CaptureMethodNames.Voltage,
    CaptureMethodNames.Current,
    CaptureMethodNames.Power,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.SERVO]: [
    CaptureMethodNames.Position,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.SENSOR]: [
    CaptureMethodNames.Readings,
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ComponentSubtypes.SWITCH]: [
    CaptureMethodNames.GetWorldPose,
    CaptureMethodNames.DoCommand,
  ],
  [ServiceSubtypes.BASE_REMOTE_CONTROL]: [CaptureMethodNames.DoCommand],
  [ServiceSubtypes.DISCOVERY]: [CaptureMethodNames.DoCommand],
  [ServiceSubtypes.MOTION]: [CaptureMethodNames.DoCommand],
  [ServiceSubtypes.NAVIGATION]: [CaptureMethodNames.DoCommand],
  [ServiceSubtypes.SHELL]: [CaptureMethodNames.DoCommand],
  [ServiceSubtypes.SLAM]: [
    CaptureMethodNames.Position,
    CaptureMethodNames.PointCloudMap,
    CaptureMethodNames.DoCommand,
  ],
  [ServiceSubtypes.VISION]: [
    CaptureMethodNames.CaptureAllFromCamera,
    CaptureMethodNames.DoCommand,
  ],
} as const;

export const getMethods = (componentName?: string): string[] => {
  if (componentName && CaptureMethodsBySubtype[componentName]) {
    return CaptureMethodsBySubtype[componentName] ?? [];
  }

  return AllCaptureMethodNames;
};

export const isAudioMimeType = (mimeType: string | undefined): boolean =>
  Boolean(mimeType?.startsWith('audio/'));

export const MIMETypes = [
  'image/vnd.viam.rgba',
  'image/vnd.viam.rgbalazy',
  'image/jpeg',
  'image/png',
  'pointcloud/pcd',
  'image/qoi',
] as const;

export const supportsDataCapture = (subtype: string) =>
  subtype in CaptureMethodsBySubtype;

export const supportsFilterSourceNames = (
  captureMethod: CaptureMethodName | undefined
) => captureMethod === CaptureMethodNames.GetImages;

export const requiresMIMEType = (
  captureMethod: CaptureMethodName | undefined
) => {
  return captureMethod === CaptureMethodNames.ReadImage;
};

export const requiresPinName = (
  captureMethod: CaptureMethodName | undefined
) => {
  return captureMethod === CaptureMethodNames.Gpios;
};

export const requiresReaderName = (
  captureMethod: CaptureMethodName | undefined
) => {
  return captureMethod === CaptureMethodNames.Analogs;
};

export const requiresCameraName = (
  captureMethod: CaptureMethodName | undefined
) => {
  return captureMethod === CaptureMethodNames.CaptureAllFromCamera;
};

export const requiresAdditionalParams = (
  captureMethod: CaptureMethodName | undefined
) =>
  requiresMIMEType(captureMethod) ||
  requiresPinName(captureMethod) ||
  requiresReaderName(captureMethod) ||
  requiresCameraName(captureMethod) ||
  supportsFilterSourceNames(captureMethod);
